import type { ScheduledJob, ServiceRunStatus, ServiceRunsPage } from '../../api/monitor';
import { Pagination } from '../../components/ui/Pagination';
import { SearchSelect } from '../../components/ui/SearchSelect';
import { runsPageLabel } from '../../lib/serviceRuns';
import { RunRow } from './RunRow';

interface RunHistoryProps {
  page: ServiceRunsPage | null;
  /** Alimenta o seletor de job; vem do cadastro, não do histórico. */
  jobs: ScheduledJob[];
  jobFilter: string | null;
  statusFilter: ServiceRunStatus | null;
  error: string | null;
  onJobChange: (job: string | null) => void;
  onStatusChange: (status: ServiceRunStatus | null) => void;
  onPageChange: (page: number) => void;
}

const STATUS_OPTIONS: { value: ServiceRunStatus; label: string }[] = [
  { value: 'error', label: 'Falhou' },
  { value: 'running', label: 'Em execução' },
  { value: 'ok', label: 'Concluído' },
];

/**
 * Histórico de execuções, paginado no servidor. Os filtros ficam aqui em cima
 * porque são a forma de responder "o que falhou": `status=error` mostra só as
 * rodadas ruins, com a mensagem de erro de cada uma na própria linha.
 */
export function RunHistory({
  page,
  jobs,
  jobFilter,
  statusFilter,
  error,
  onJobChange,
  onStatusChange,
  onPageChange,
}: RunHistoryProps) {
  const filtered = jobFilter !== null || statusFilter !== null;

  return (
    <section className="mt-4">
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-[14px] font-extrabold">Execuções</h2>

        <SearchSelect
          size="sm"
          value={jobFilter ?? ''}
          onChange={(value) => onJobChange(value || null)}
          options={jobs.map((job) => ({ value: job.job, label: job.label }))}
          clearable
          placeholder="Todos os serviços"
          searchPlaceholder="Buscar serviço…"
          aria-label="Filtrar por serviço"
        />

        <SearchSelect
          size="sm"
          value={statusFilter ?? ''}
          onChange={(value) => onStatusChange((value || null) as ServiceRunStatus | null)}
          options={STATUS_OPTIONS}
          clearable
          placeholder="Todos os resultados"
          searchPlaceholder="Buscar resultado…"
          aria-label="Filtrar por resultado"
        />
      </div>

      {error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {error}
        </div>
      ) : null}

      <div className="border-line-200 overflow-hidden rounded-[14px] border bg-white">
        {page && page.runs.length > 0 ? (
          page.runs.map((run) => <RunRow key={run.id} run={run} />)
        ) : (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            {filtered
              ? 'Nenhuma execução para este filtro.'
              : 'Nenhum serviço executado ainda.'}
          </div>
        )}
      </div>

      {page ? (
        <Pagination
          page={page.page}
          totalPages={page.totalPages}
          label={runsPageLabel(page.page, page.pageSize, page.total)}
          disabled={false}
          onChange={onPageChange}
        />
      ) : null}
    </section>
  );
}
